'use client';

import React, { useEffect } from 'react';

import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import HeroBadge from './components/HeroBadge';
import HeroImage from './components/HeroImage';
import HeroCTA from './components/HeroCTA';
import BeforePhoto from './components/BeforePhoto';
import StoryText from './components/StoryText';
import PainPointList from './components/PainPointList';
import TimelineCard from './components/TimelineCard';
import TimelineConnector from './components/TimelineConnector';
import ObstacleCard from './components/ObstacleCard';
import BeforeAfterSlider from './components/BeforeAfterSlider';
import MetricCard from './components/MetricCard';
import CTAButton from './components/CTAButton';
import TestimonialSnippet from './components/TestimonialSnippet';
import Icon from '@/components/ui/AppIcon';

// Mock Data
const painPoints = [
{
  id: 'pain_1',
  text: "Couldn\'t keep up with my kids - exhausted by mid-morning despite being a busy mom who needed natural energy"
},
{
  id: 'pain_2',
  text: 'Felt invisible in family photos and avoided mirrors, struggling with weight loss as a woman over 50'
},
{
  id: 'pain_3',
  text: 'Believed my slow metabolism as a senior meant I could never achieve a healthy lifestyle again'
},
{
  id: 'pain_4',
  text: "Worried I\'d miss my children\'s milestones because I couldn\'t maintain the energy mothers need"
}];


const timelineMilestones = [
{
  id: 'month_1',
  month: 'Month 1',
  title: 'First Family Hike',
  description: 'Natural energy kicked in - made it to the top without stopping, thanks to metabolism support',
  iconName: 'MapIcon'
},
{
  id: 'month_3',
  month: 'Month 3',
  title: 'Playing at the Park',
  description: 'Ran with my kids all afternoon - the Fit at 50 program gave me stamina I never had',
  iconName: 'HeartIcon'
},
{
  id: 'month_6',
  month: 'Month 6',
  title: 'Completed 5K',
  description: 'Crossed the finish line proving weight loss for women over 50 is absolutely possible',
  iconName: 'TrophyIcon'
}];


const obstacles = [
{
  id: 'obstacle_1',
  obstacle: '"I have no time as a busy mom"',
  solution: '20-minute morning routine that fits any mother\'s schedule - natural energy all day',
  iconName: 'ClockIcon'
},
{
  id: 'obstacle_2',
  obstacle: '"My metabolism is too slow at 50"',
  solution: 'Metabolism support for seniors - our Fit at 50 program resets your body in 6 weeks',
  iconName: 'BoltIcon'
},
{
  id: 'obstacle_3',
  obstacle: '"I can\'t give up my favorite foods"',
  solution: 'Enjoyed pizza every Friday - still achieved weight loss for women over 50',
  iconName: 'CakeIcon'
}];


const metrics = [
{
  id: 'metric_1',
  value: '50 lbs',
  label: 'Weight Loss Over 50',
  iconName: 'ScaleIcon',
  colorClass: 'bg-accent/10 text-accent'
},
{
  id: 'metric_2',
  value: 'Size 42 → 36',
  label: 'Transformation',
  iconName: 'SparklesIcon',
  colorClass: 'bg-primary/10 text-primary'
},
{
  id: 'metric_3',
  value: 'Normalized',
  label: 'Blood Pressure',
  iconName: 'HeartIcon',
  colorClass: 'bg-success/10 text-success'
},
{
  id: 'metric_4',
  value: '10x More',
  label: 'Natural Energy',
  iconName: 'BoltIcon',
  colorClass: 'bg-warning/10 text-warning'
},
{
  id: 'metric_5',
  value: '5+ Hours',
  label: 'Sustained Energy for Moms',
  iconName: 'UserGroupIcon',
  colorClass: 'bg-primary/10 text-primary'
}];


export default function Homepage() {
  // Scroll Reveal Animation
  useEffect(() => {
    const reveals = document.querySelectorAll('.reveal');
    if (!reveals?.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    reveals?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <Header />
      <main className="min-h-screen bg-background">
        {/* HERO SECTION */}
        <section className="relative w-full h-[92vh] bg-slate-900 overflow-hidden">
          {/* Background Image */}
          <HeroImage
            src="https://img.rocket.new/generatedImages/rocket_gen_img_18b29f5a8-1771226352696.png"
            alt="Energetic woman over 50 playing joyfully with children outdoors, demonstrating natural energy and vitality" />
          

          {/* Top Badges */}
          <nav className="absolute top-0 left-0 right-0 z-50 px-4 sm:px-6 md:px-10 py-4 sm:py-6 md:py-8 flex justify-between items-start animate-enter delay-500">
            <div className="flex items-center gap-2 sm:gap-3">
              <HeroBadge
                icon={<Icon name="CheckCircleIcon" size={16} className="text-success" variant="solid" />}
                text="Weight Loss for Women Over 50" />
              
            </div>
            <div className="hidden md:flex items-center gap-3">
              <HeroBadge icon={<Icon name="ClockIcon" size={16} />} text="Fit at 50 Program" />
            </div>
          </nav>

          {/* Main Content */}
          <div className="relative w-full h-full flex flex-col justify-end pb-8 sm:pb-12 md:pb-20 pt-20 sm:pt-24 md:pt-32 px-4 sm:px-8 md:px-16 pointer-events-none">
            <div className="pointer-events-auto space-y-3 sm:space-y-4 md:space-y-6">
              {/* Badge */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 animate-enter delay-100">
                <div className="flex items-center gap-2 bg-white text-foreground px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                  <Icon name="UserIcon" size={16} className="text-primary" />
                  <span>Busy Mom of 3</span>
                </div>
              </div>

              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-serif font-light text-white tracking-tight leading-tight drop-shadow-xl max-w-4xl animate-enter delay-200">
                She Found Natural Energy at 50.{' '}
                <span className="text-primary">You Can Too.</span>
              </h1>

              {/* Subheadline */}
              <p className="text-white/90 text-sm sm:text-base md:text-lg lg:text-xl font-light max-w-xl leading-relaxed animate-enter delay-300">
                From exhausted busy mom to vibrant and unstoppable - discover how one mother achieved 
                weight loss for women over 50 and reclaimed her healthy lifestyle.
              </p>

              {/* CTAs */}
              <div className="pt-2 sm:pt-4 animate-enter delay-500">
                <HeroCTA
                  primaryText="Join Fit at 50 Program"
                  secondaryText="Watch Video" />
                
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1: THE STRUGGLE */}
        <section className="bg-background w-full px-4 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 lg:py-28">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            {/* Before Photo */}
            <div className="reveal">
              <BeforePhoto
                src="https://images.pexels.com/photos/4474035/pexels-photo-4474035.jpeg?auto=compress&cs=tinysrgb&w=1200"
                alt="Tired woman over 50 sitting on couch looking exhausted, before discovering natural energy and metabolism support" />
              
            </div>

            {/* Story Text */}
            <div className="reveal reveal-delay-200">
              <span className="inline-block px-4 py-1.5 rounded-full border border-border text-xs font-semibold text-muted-foreground tracking-wide uppercase mb-6 hover:text-foreground hover:border-foreground/30 transition-all duration-300 cursor-default">
                The Struggle
              </span>
              <StoryText
                title="At 50, I Was a Busy Mom Running on Empty"
                paragraphs={[
                "I remember the exact moment I knew something had to change. My 12-year-old asked me to play basketball, and I couldn't make it through 5 minutes. As a busy mom, I desperately needed natural energy, but I was running on fumes every single day.",
                "I'd spent years putting everyone else first - my career, my kids, my family. Weight loss for women over 50 seemed impossible. Everyone said my metabolism was too slow, that seniors couldn't achieve real transformation. I'd given up on a healthy lifestyle for mothers like me.",
                "I felt invisible. I avoided family photos. I had no energy by mid-afternoon. And worst of all, I was terrified I wouldn't be healthy enough to see my children grow up. I needed metabolism support for seniors, but didn't know where to start."]} />
              
              
              <PainPointList points={painPoints} />
            </div>
          </div>
        </section>

        {/* SECTION 2: THE JOURNEY */}
        <section className="bg-muted w-full px-4 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16 reveal">
              <span className="inline-block px-4 py-1.5 rounded-full border border-border text-xs font-semibold text-muted-foreground tracking-wide uppercase mb-4 hover:text-foreground hover:border-foreground/30 transition-all duration-300 cursor-default">
                The Journey
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-light text-foreground tracking-tight leading-tight mb-4 sm:mb-6 px-4 hover:text-accent transition-colors duration-300 cursor-default">
                The Fit at 50 Program: Six Months That Changed Everything
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
                Real metabolism support for seniors, natural energy for busy moms, and a healthy lifestyle for mothers - one step at a time
              </p>
            </div>

            {/* Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-center">
              {timelineMilestones?.map((milestone, index) =>
              <React.Fragment key={milestone?.id}>
                  <div className={`reveal reveal-delay-${index * 100}`}>
                    <TimelineCard
                    month={milestone?.month}
                    title={milestone?.title}
                    description={milestone?.description}
                    iconName={milestone?.iconName} />
                  
                  </div>
                  {index < timelineMilestones?.length - 1 && <TimelineConnector />}
                </React.Fragment>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 3: OVERCOMING OBSTACLES */}
        <section className="bg-background w-full px-4 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16 reveal">
              <span className="inline-block px-4 py-1.5 rounded-full border border-border text-xs font-semibold text-muted-foreground tracking-wide uppercase mb-4 hover:text-foreground hover:border-foreground/30 transition-all duration-300 cursor-default">
                Overcoming Obstacles
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-light text-foreground tracking-tight leading-tight mb-4 sm:mb-6 px-4 hover:text-accent transition-colors duration-300 cursor-default">
                Your Excuses? <span className="text-accent">She Had Them Too.</span>
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
                How the Fit at 50 program turned every obstacle into a stepping stone for busy moms
              </p>
            </div>

            {/* Obstacle Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {obstacles?.map((obstacle, index) =>
              <div key={obstacle?.id} className={`reveal reveal-delay-${index * 100}`}>
                  <ObstacleCard
                  obstacle={obstacle?.obstacle}
                  solution={obstacle?.solution}
                  iconName={obstacle?.iconName} />
                
                </div>
              )}
            </div>
          </div>
        </section>

        {/* SECTION 4: THE TRANSFORMATION */}
        <section className="bg-muted w-full px-4 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 lg:py-28">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12 sm:mb-16 reveal">
              <span className="inline-block px-4 py-1.5 rounded-full border border-border text-xs font-semibold text-muted-foreground tracking-wide uppercase mb-4 hover:text-foreground hover:border-foreground/30 transition-all duration-300 cursor-default">
                The Transformation
              </span>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif font-light text-foreground tracking-tight leading-tight mb-4 sm:mb-6 px-4 hover:text-accent transition-colors duration-300 cursor-default">
                Weight Loss for Women Over 50: The Results Speak
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto px-4">
                50 pounds lighter, natural energy gained, and a healthy lifestyle for mothers fully reclaimed
              </p>
            </div>

            {/* Before/After Slider */}
            <div className="mb-8 sm:mb-12 reveal reveal-delay-100">
              <BeforeAfterSlider
                beforeSrc="https://images.pexels.com/photos/4474035/pexels-photo-4474035.jpeg?auto=compress&cs=tinysrgb&w=1200"
                beforeAlt="Woman before weight loss transformation - tired busy mom needing natural energy and metabolism support"
                afterSrc="https://images.pexels.com/photos/4473622/pexels-photo-4473622.jpeg?auto=compress&cs=tinysrgb&w=1200"
                afterAlt="Woman after Fit at 50 program - energetic mother over 50 with natural energy playing with children" />
              
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 reveal reveal-delay-200">
              {metrics?.map((metric) =>
              <MetricCard
                key={metric?.id}
                value={metric?.value}
                label={metric?.label}
                iconName={metric?.iconName}
                colorClass={metric?.colorClass} />

              )}
            </div>
          </div>
        </section>

        {/* SECTION 5: YOUR TRANSFORMATION STARTS HERE (CTA) */}
        <section className="bg-primary-dark w-full px-4 sm:px-6 md:px-16 py-12 sm:py-16 md:py-20 lg:py-28 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5" />

          <div className="relative z-10 max-w-3xl mx-auto reveal">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-light text-white mb-4 sm:mb-6 tracking-tight leading-tight px-4 hover:text-white/90 transition-colors duration-300 cursor-default">
              Your Healthy Lifestyle Starts Here
            </h2>
            <p className="text-white/90 text-base sm:text-lg md:text-xl mb-8 sm:mb-10 leading-relaxed px-4">
              Join 10,000+ busy moms who discovered natural energy, achieved weight loss for women over 50, 
              and transformed their lives with metabolism support for seniors through our Fit at 50 program.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-8 sm:mb-12 px-4">
              <CTAButton text="Start Fit at 50 Program" />
              <CTAButton
                text="See More Success Stories"
                variant="secondary" />
              
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-8 text-white/80 text-xs sm:text-sm mb-8 sm:mb-10 px-4">
              <div className="flex items-center gap-2">
                <Icon name="CheckCircleIcon" size={18} className="text-success sm:w-5 sm:h-5" variant="solid" />
                <span>Free transformation assessment</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="ClockIcon" size={18} className="sm:w-5 sm:h-5" />
                <span>Start in under 5 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={18} className="sm:w-5 sm:h-5" />
                <span>100% privacy guaranteed</span>
              </div>
            </div>

            {/* Testimonial Snippet */}
            <div className="max-w-xl mx-auto reveal reveal-delay-200 px-4">
              <TestimonialSnippet
                quote="The Fit at 50 program gave me natural energy I never thought possible. Weight loss for women over 50 isn't a myth - it's real, and I'm living proof."
                name="Jennifer M."
                age={52}
                photoSrc="/assets/images/jennifer_testimonial_photo.png"
                photoAlt="Portrait of energetic woman over 50 who achieved healthy lifestyle through metabolism support" />
              
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );


}